import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Calculator, TrendingUp, CheckCircle2, AlertTriangle, Info, Sparkles, Shield, FileText, Target, RefreshCcw, Check, X, Percent } from 'lucide-react';
import { SECTEURS } from './SimulateurOscopConstants';

export default function SimulateurResults({ result, resetSimulation }) {
  if (!result) return null;

  const { results, evaluation, indicateurs_ess, secteur_info, territoire_info, mentions_legales, restrictions } = result;

  const restrictionWarnings = restrictions?.warnings || [];
  const softRestrictions = result.soft_restrictions_applied || [];
  const essValidations = result.ess_validations_required || [];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header avec status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-full bg-gradient-to-br ${
            result.status === 'ok_with_limits'
              ? 'from-amber-500 to-orange-500'
              : SECTEURS[result.secteur]?.color || 'from-blue-500 to-indigo-500'
          }`}>
            {result.status === 'ok_with_limits'
              ? <AlertTriangle className="h-6 w-6 text-white" />
              : <CheckCircle2 className="h-6 w-6 text-white" />
            }
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {result.status === 'ok_with_limits' ? 'Simulation avec ajustements' : 'Simulation réussie'}
            </h3>
            <p className="text-sm text-gray-500">
              {territoire_info?.icon} {result.territoire} • {secteur_info?.icon} {result.secteur} • {result.type_acteur}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={resetSimulation}>
          <RefreshCcw className="h-4 w-4 mr-1" /> Modifier
        </Button>
      </div>

      {/* Restrictions SOFT appliquées */}
      {softRestrictions.length > 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-800 font-bold">Ajustements automatiques appliqués</AlertTitle>
          <AlertDescription className="text-amber-700">
            <ul className="space-y-2 mt-2">
              {softRestrictions.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Badge variant="outline" className="text-amber-200 border-amber-600 bg-amber-800 text-white text-xs font-semibold">
                    {r.id}
                  </Badge>
                  <span className="text-sm">{r.message?.fr || r.name}</span>
                </li>
              ))}
            </ul>
            {restrictions?.adjustments_made && Object.keys(restrictions.adjustments_made).length > 0 && (
              <div className="mt-3 p-2 bg-amber-100 rounded-lg">
                <p className="text-xs font-semibold text-amber-800">Paramètres ajustés :</p>
                <ul className="text-xs text-amber-700 mt-1">
                  {restrictions.adjustments_made.duree_mois && (
                    <li>• Durée limitée à {restrictions.adjustments_made.duree_mois/12} ans (au lieu de {restrictions.adjustments_made.duree_mois_original/12} ans)</li>
                  )}
                  {restrictions.adjustments_made.recommended_apport && (
                    <li>• Apport recommandé : {restrictions.adjustments_made.recommended_apport.toLocaleString('fr-FR')} €</li>
                  )}
                </ul>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Validations ESS requises */}
      {essValidations.length > 0 && (
        <Alert className="border-purple-300 bg-purple-50">
          <Shield className="h-5 w-5 text-purple-600" />
          <AlertTitle className="text-purple-800 font-bold flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Validation coopérative requise
          </AlertTitle>
          <AlertDescription className="text-purple-700">
            <ul className="space-y-3 mt-3">
              {essValidations.map((v, i) => (
                <li key={i} className="p-3 bg-purple-600 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <Badge className="bg-white text-slate-800 text-white font-semibold">{v.id}</Badge>
                    <Badge variant="outline" className="text-white border-purple-300">
                      Niveau: {v.level}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium text-white">{v.message?.fr || v.name}</p>
                  <p className="text-xs mt-1 text-purple-200">
                    Validation par : <strong>{v.validator?.replace('_', ' ')}</strong>
                  </p>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Warnings généraux */}
      {restrictionWarnings.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Avertissements</AlertTitle>
          <AlertDescription className="text-amber-700">
            <ul className="list-disc list-inside space-y-1">
              {restrictionWarnings.map((w, i) => (
                <li key={i} className="text-sm">{w.message?.fr || w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Indicateurs clés */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={`border-2 ${
          evaluation?.eligibilite === 'eligible' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
        }`}>
          <CardContent className="p-4 text-center">
            <Target className={`h-8 w-8 mx-auto mb-2 ${
              evaluation?.eligibilite === 'eligible' ? 'text-green-600' : 'text-amber-600'
            }`} />
            <p className="text-xs text-gray-500 uppercase">Éligibilité</p>
            <p className={`text-sm font-bold ${
              evaluation?.eligibilite === 'eligible' ? 'text-green-700' : 'text-amber-700'
            }`}>
              {evaluation?.eligibilite_label}
            </p>
          </CardContent>
        </Card>

        <Card className={`border-2 ${
          evaluation?.niveau_effort === 'faible' ? 'border-green-200 bg-green-50' :
          evaluation?.niveau_effort === 'modéré' ? 'border-blue-200 bg-blue-50' :
          'border-red-200 bg-red-50'
        }`}>
          <CardContent className="p-4 text-center">
            <TrendingUp className={`h-8 w-8 mx-auto mb-2 ${
              evaluation?.niveau_effort === 'faible' ? 'text-green-600' :
              evaluation?.niveau_effort === 'modéré' ? 'text-blue-600' :
              'text-red-600'
            }`} />
            <p className="text-xs text-gray-500 uppercase">Effort financier</p>
            <p className={`text-sm font-bold capitalize ${
              evaluation?.niveau_effort === 'faible' ? 'text-green-700' :
              evaluation?.niveau_effort === 'modéré' ? 'text-blue-700' :
              'text-red-700'
            }`}>
              {evaluation?.niveau_effort}
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200 bg-purple-50">
          <CardContent className="p-4 text-center">
            <Percent className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <p className="text-xs text-gray-500 uppercase">Taux d'effort</p>
            <p className="text-sm font-bold text-purple-700">{results?.effort_financier}</p>
          </CardContent>
        </Card>
      </div>

      {/* Résultats financiers */}
      <Card className="overflow-hidden">
        <CardHeader className={`bg-gradient-to-r ${SECTEURS[result.secteur]?.color || 'from-indigo-600 to-purple-600'} text-white`}>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Résultats de votre simulation
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Base à financer</p>
              <p className="text-xl font-bold">{results?.base_financee?.toLocaleString('fr-FR')} €</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-gray-500">Dotation fonds</p>
              <p className="text-xl font-bold text-green-700">{results?.dotation_fonds?.toLocaleString('fr-FR')} €</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-gray-500">Bonus ESS</p>
              <p className="text-xl font-bold text-blue-700">-{results?.total_bonus_ess?.toLocaleString('fr-FR')} €</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-gray-500">Taux solidaire</p>
              <p className="text-xl font-bold text-purple-700">{results?.taux_solidaire_pourcent}</p>
            </div>
          </div>

          {/* Redevance mensuelle */}
          <div className={`bg-gradient-to-r ${SECTEURS[result.secteur]?.color || 'from-indigo-600 to-purple-600'} rounded-2xl p-6 text-white text-center`}>
            <p className="text-sm opacity-90 uppercase tracking-wide mb-2">Redevance mensuelle estimée</p>
            <div className="flex items-center justify-center gap-3">
              <Sparkles className="h-8 w-8 opacity-80" />
              <span className="text-5xl font-bold">
                {results?.redevance_mensuelle?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </span>
              <span className="text-xl opacity-80">/mois</span>
            </div>
            <p className="text-sm opacity-75 mt-2">
              Sur {result.inputs_recap?.duree_annees} ans ({result.inputs_recap?.duree_mois} mois)
            </p>
          </div>

          {/* Indicateurs ESS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              {indicateurs_ess?.solidarite_mutualisee ?
                <Check className="h-5 w-5 text-green-600" /> :
                <X className="h-5 w-5 text-red-400" />
              }
              <span className="text-sm">Solidarité mutualisée</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              {indicateurs_ess?.prevention_active ?
                <Check className="h-5 w-5 text-green-600" /> :
                <X className="h-5 w-5 text-red-400" />
              }
              <span className="text-sm">Prévention active</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              {indicateurs_ess?.adhesion_ftpe ?
                <Check className="h-5 w-5 text-green-600" /> :
                <X className="h-5 w-5 text-red-400" />
              }
              <span className="text-sm">Adhésion FTPE</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
              <Shield className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium">{indicateurs_ess?.fonds_garantie}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mentions légales */}
      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Mentions légales</AlertTitle>
        <AlertDescription className="text-blue-700">
          <ul className="list-disc list-inside space-y-1 mt-2">
            {mentions_legales?.map((m, i) => <li key={i} className="text-sm">{m}</li>)}
          </ul>
        </AlertDescription>
      </Alert>

      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={resetSimulation}>
          <RefreshCcw className="h-4 w-4 mr-2" /> Nouvelle simulation
        </Button>
        <Button className={`flex-1 bg-gradient-to-r ${SECTEURS[result.secteur]?.color || 'from-green-600 to-emerald-600'}`}>
          <FileText className="h-4 w-4 mr-2" /> Contacter un conseiller
        </Button>
      </div>
    </div>
  );
}
